<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ConversationMenu from '@/features/story-conversation/components/ConversationMenu.vue'
import AppearanceEditor from '@/features/appearance/components/AppearanceEditor.vue'
import ChevronDownIcon from 'vue-material-design-icons/ChevronDown.vue'
import BookArrowUpOutlineIcon from 'vue-material-design-icons/BookArrowUpOutline.vue'
import BookPlayOutlineIcon from 'vue-material-design-icons/BookPlayOutline.vue'
import ThermometerIcon from 'vue-material-design-icons/Thermometer.vue'
import ActionMenuBotPanel from '@/components/ActionMenuBotPanel.vue'
import ActionMenuBotConfigPanel from '@/components/ActionMenuBotConfigPanel.vue'
import type { Bot } from '@/types/domain'

const props = withDefaults(
  defineProps<{
    bot: Bot | null
    selectBot: (id: number | null) => Promise<unknown>
    saveGenerationSettings?: (values: {
      context_size: number
      max_tokens: number
      temperature: number
    }) => Promise<boolean>
    sections?: { bots?: boolean; generation?: boolean; appearance?: boolean }
    disabled?: boolean
  }>(),
  { disabled: false, sections: () => ({}) }
)
type ConversationMenuSection = 'none' | 'bots' | 'settings' | 'appearance'

// Conversation menu (feather icon in top bar)
// Which config surface is drilled into. The menu opens to 'none' — resting
// summary rows — and expands one section at a time (single-expand accordion).
const convMenuSection = ref<ConversationMenuSection>('none')
// True while a bot edit/import page is open inside the config panel; deepens the
// whole-surface warming (--drill) one more level.
const botFormOpen = ref<boolean>(false)
watch(() => props.sections, () => {
  const section = convMenuSection.value === 'settings' ? 'generation' : convMenuSection.value
  if (section !== 'none' && props.sections[section] === false) {
    convMenuSection.value = 'none'
    botFormOpen.value = false
  }
}, { deep: true })


const toggleConvMenuSection = (section: Exclude<ConversationMenuSection, 'none'>): void => {
  convMenuSection.value = convMenuSection.value === section ? 'none' : section
}

// Engagement-depth warmth, driven from a single number so the whole surface
// shifts as one gesture (never per-element): 0 at rest, 1 when a section is
// open, ~1.6 when a bot form page is open on top of the list.
const convDrillLevel = computed<number>(() => {
  if (convMenuSection.value === 'none') return 0
  if (convMenuSection.value === 'bots' && botFormOpen.value) return 1.6
  return 1
})

const activeBot = computed(() => props.bot)

const activeBotName = computed<string>(() => {
  const bot = activeBot.value
  if (!bot) return 'No bot selected'
  return bot.name || bot.model || 'Unnamed bot'
})
const activeBotModel = computed<string>(() => {
  const bot = activeBot.value
  if (!bot) return 'Tap to choose a bot'
  if (bot.is_openrouter) return 'In-house bot'
  return bot.model || 'Unknown model'
})
// Generation preview, split so each stat can carry its own inline icon (context
// / max-tokens / temperature) instead of a shared label line beneath.
const genContext = computed<string>(() => {
  const bot = activeBot.value
  if (!bot) return '—'
  const raw = bot.context_size ?? 0
  return raw >= 1000 ? `${Math.round(raw / 1000)}k` : String(raw)
})
const genMaxTokens = computed<string>(() => {
  const bot = activeBot.value
  if (!bot) return '—'
  return (bot.max_tokens ?? 0) === 0 ? 'Unlimited' : String(bot.max_tokens)
})
const genTemperature = computed<string>(() => {
  const bot = activeBot.value
  if (!bot) return '—'
  return (bot.temperature ?? 0).toFixed(1)
})
</script>

<template>
  <ConversationMenu
    :depth="convDrillLevel"
    v-slot="{ close }"
    @close="convMenuSection = 'none'"
  >
    <template v-if="sections.bots !== false">
    <!-- Row A · conversation bot -->
    <button
      type="button"
      class="conv-menu__row"
      :class="{ 'conv-menu__row--open': convMenuSection === 'bots' }"
      :aria-expanded="convMenuSection === 'bots'"
      @click="toggleConvMenuSection('bots')"
    >
      <span class="conv-menu__row-text">
        <span class="conv-menu__eyebrow">Bot</span>
        <span class="conv-menu__row-value">{{ activeBotName }}</span>
        <span class="conv-menu__row-sub">{{ activeBotModel }}</span>
      </span>
      <ChevronDownIcon
        class="conv-menu__chevron"
        :size="20"
        aria-hidden="true"
      />
    </button>
    <div
      class="conv-menu__drawer"
      :inert="convMenuSection !== 'bots' ? true : undefined"
      :class="{ 'conv-menu__drawer--open': convMenuSection === 'bots' }"
    >
      <div class="conv-menu__drawer-inner">
        <ActionMenuBotConfigPanel
          :active-bot-id="activeBot?.id"
          :select-conversation-bot="selectBot"
          :disabled="disabled"
          @form-open-change="botFormOpen = $event"
        />
      </div>
    </div>

    </template>
    <div v-if="sections.bots !== false && (sections.generation !== false || sections.appearance !== false)" class="conv-menu__seam" aria-hidden="true" />
    <template v-if="sections.generation !== false">
    <!-- Row B · generation settings -->
    <button
      type="button"
      class="conv-menu__row"
      :class="{ 'conv-menu__row--open': convMenuSection === 'settings' }"
      :aria-expanded="convMenuSection === 'settings'"
      @click="toggleConvMenuSection('settings')"
    >
      <span class="conv-menu__row-text">
        <span class="conv-menu__eyebrow">Generation</span>
        <span class="conv-menu__gen-stats">
          <span
            class="conv-menu__gen-stat"
            title="Context"
          >
            <BookArrowUpOutlineIcon
              class="conv-menu__gen-icon"
              :size="17"
              aria-hidden="true"
            />
            {{ genContext }}
          </span>
          <span
            class="conv-menu__gen-sep"
            aria-hidden="true"
            >·</span
          >
          <span
            class="conv-menu__gen-stat"
            title="Max output tokens"
          >
            <BookPlayOutlineIcon
              class="conv-menu__gen-icon"
              :size="17"
              aria-hidden="true"
            />
            {{ genMaxTokens }}
          </span>
          <span
            class="conv-menu__gen-sep"
            aria-hidden="true"
            >·</span
          >
          <span
            class="conv-menu__gen-stat"
            title="Temperature"
          >
            <ThermometerIcon
              class="conv-menu__gen-icon"
              :size="17"
              aria-hidden="true"
            />
            {{ genTemperature }}
          </span>
        </span>
      </span>
      <ChevronDownIcon
        class="conv-menu__chevron"
        :size="20"
        aria-hidden="true"
      />
    </button>
    <div
      class="conv-menu__drawer"
      :inert="convMenuSection !== 'settings' ? true : undefined"
      :class="{ 'conv-menu__drawer--open': convMenuSection === 'settings' }"
    >
      <div class="conv-menu__drawer-inner">
        <ActionMenuBotPanel
          :bot="activeBot"
          :save="saveGenerationSettings"
          :disabled="disabled"
        />
      </div>
    </div>
    </template>
    <div v-if="sections.generation !== false && sections.appearance !== false" class="conv-menu__seam" aria-hidden="true" />
    <template v-if="sections.appearance !== false">
    <button
      type="button"
      class="conv-menu__row"
      :class="{ 'conv-menu__row--open': convMenuSection === 'appearance' }"
      :aria-expanded="convMenuSection === 'appearance'"
      @click="toggleConvMenuSection('appearance')"
    >
      <span class="conv-menu__row-text">
        <span class="conv-menu__eyebrow">Appearance</span>
        <span class="conv-menu__row-value">Theme, font &amp; text colors</span>
        <span class="conv-menu__row-sub">Make the story your own</span>
      </span>
      <ChevronDownIcon class="conv-menu__chevron" :size="20" aria-hidden="true" />
    </button>
    <div
      class="conv-menu__drawer"
      :inert="convMenuSection !== 'appearance' ? true : undefined"
      :class="{ 'conv-menu__drawer--open': convMenuSection === 'appearance' }"
    >
      <div class="conv-menu__drawer-inner">
        <AppearanceEditor v-if="convMenuSection === 'appearance'" />
      </div>
    </div>
    </template>
    <slot :close="close" />
  </ConversationMenu>
</template>
